import { useEffect, useMemo, useState } from "react";
import {
  ageFromBucket,
  catalogVolumeToBodymap,
  normalizeVolumeByMuscle,
  sessionEnergyReadout,
  volumeBySlugToHeatmap,
  type AgeBucket,
  type BodyFigure,
  type HeatmapRamp,
  type Session,
  type Workout,
} from "@heraion/shared";
import { useSessionNotes, workoutReader } from "@heraion/ui";

import { apiFetch } from "./api";
import { useExerciseCatalog } from "./logger/useExerciseCatalog";
import { fetchSessionDetail } from "./useSessionLoader";
import { useSessionResult } from "./useSessionResult";

// The same read Edit/Repeat and «Save as model» use: the session with its sets.
const readWorkout = workoutReader(fetchSessionDetail);

// The Workouts detail pane's data for the selected session, split out of
// WorkoutsScreen.tsx (file-size cap, #368): the summary and its notes, the
// energy readout, the figure and the muscle heatmap. Same derivations.
export function useWorkoutDetailData(
  selectedId: string | null,
  items: Session[] | null,
  doc: Record<string, unknown> | null,
  heatmapRamp: HeatmapRamp,
) {
  // A correction keeps the session id (#38): its updatedAt is what changes.
  const selectedVersion = selectedId
    ? items?.find((s) => s.id === selectedId)?.updatedAt
    : undefined;
  const detail = useSessionResult(selectedId, selectedVersion);
  const detailNotes = useSessionNotes(apiFetch, selectedId, selectedVersion);
  // The full Session (with the estimate and HR): the SessionResult detail
  // lacks them; the list items ARE full Sessions, so pluck the selected one
  // (selection always comes from the loaded list).
  const selectedSession = useMemo(
    () =>
      selectedId ? (items?.find((s) => s.id === selectedId) ?? null) : null,
    [selectedId, items],
  );
  // #290: heart rate shows only on a session with a cardio set, and the list
  // item carries no sets, so they are read here for the selected session.
  // Keyed by id and version, so a stale read never lands on another session.
  const [loaded, setLoaded] = useState<{ key: string; workout: Workout } | null>(null);
  const setsKey = selectedId ? `${selectedId}@${selectedVersion ?? ""}` : null;
  useEffect(() => {
    if (setsKey === null || selectedId === null) return;
    let alive = true;
    readWorkout(selectedId).then(
      (workout) => {
        if (alive) setLoaded({ key: setsKey, workout });
      },
      () => {
        // A failed read leaves the heart rate hidden, nothing else changes.
      },
    );
    return () => {
      alive = false;
    };
  }, [setsKey, selectedId]);
  // #290: an HIIT is told by its exercises' movement pattern, which the sets
  // do not carry. Without the catalog the readout reads cardio sets only.
  const { patternsBySlug } = useExerciseCatalog();
  const entries = loaded !== null && loaded.key === setsKey ? loaded.workout.entries : [];
  // One shared derivation of the readout, same as the mobile logger (HC-8
  // B2c). Until the sets arrive `entries` is empty: no cardio set, no heart
  // rate, the same rule as the mobile.
  const energyReadout = selectedSession
    ? sessionEnergyReadout(
        { ...selectedSession, entries },
        { ageYears: ageFromBucket(doc?.["ageBucket"] as AgeBucket | undefined), patternsBySlug },
      )
    : null;
  const figure = doc?.["preferredFigure"] as BodyFigure | undefined;

  // Muscle heatmap for the selected session, same derivation as the mobile
  // post-session card (volumeByMuscle → catalog vocab → 5-bucket ramp), so the
  // web session detail paints the identical figure.
  const bodyMapValue = useMemo(() => {
    if (detail.result === null) return null;
    return volumeBySlugToHeatmap(
      catalogVolumeToBodymap(normalizeVolumeByMuscle(detail.result.volumeByMuscle)),
      heatmapRamp,
    );
  }, [detail.result, heatmapRamp]);

  return { detail, detailNotes, energyReadout, figure, bodyMapValue };
}
